import { Button, Card, Col, Descriptions, Popconfirm, Row, Space, Spin, Tag, message } from 'antd';
import { CalendarOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { projectsApi } from '../../../api/endpoints/projects';
import { activitiesApi } from '../../../api/endpoints/activities';
import { salesApi } from '../../../api/endpoints/sales';
import { OwnerType } from '@never2busy/shared';
import { PageHeader } from '../../../components/common/PageHeader';
import { KeywordsManager } from '../../../components/common/KeywordsManager';

export function ProjectDetailPage(): JSX.Element {
  const { id } = useParams();
  const projectId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.findOne(projectId),
  });

  const activitiesQuery = useQuery({
    queryKey: ['activities', { projectId }],
    queryFn: () => activitiesApi.list({ projectId, page: 1, pageSize: 20 }),
    enabled: !!projectId,
  });

  const salesQuery = useQuery({
    queryKey: ['sales', { projectId }],
    queryFn: () => salesApi.list({ projectId, page: 1, pageSize: 20 }),
    enabled: !!projectId,
  });

  const removeMutation = useMutation({
    mutationFn: () => projectsApi.remove(projectId),
    onSuccess: () => {
      message.success('Proyecto eliminado');
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate('/proyectos');
    },
    onError: () => message.error('No se pudo eliminar el proyecto'),
  });

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }
  if (!data) return <div>Proyecto no encontrado</div>;

  return (
    <>
      <PageHeader
        title={data.projectNumber ? `${data.projectNumber} · ${data.name}` : data.name}
        crumbs={[{ title: 'Proyectos', to: '/proyectos' }, { title: data.name }]}
        extra={
          <Space wrap>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => navigate(`/actividades/nueva?projectId=${data.id}`)}
            >
              + Actividad
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/proyectos/${data.id}/editar`)}
            >
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar este proyecto?"
              okText="Sí, eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => removeMutation.mutate()}
            >
              <Button danger icon={<DeleteOutlined />} loading={removeMutation.isPending}>
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Datos">
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Estado">
                {data.status ? <Tag>{data.status}</Tag> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Inicio">
                {data.startDate ? dayjs(data.startDate).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Fin">
                {data.endDate ? dayjs(data.endDate).format('DD/MM/YYYY') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Monto">
                {data.amount
                  ? `${parseFloat(data.amount).toLocaleString('es-MX')} ${data.currency?.code ?? ''}`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Horas planeadas">
                {data.plannedHours ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Horas usadas">{data.spentHours ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Proyecto padre">
                {data.parent ? (
                  <Link to={`/proyectos/${data.parent.id}`}>{data.parent.name}</Link>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
            </Descriptions>
            {data.notes && (
              <div style={{ marginTop: 16 }}>
                <strong>Notas:</strong>
                <div style={{ whiteSpace: 'pre-wrap' }}>{data.notes}</div>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <strong>Etiquetas:</strong>
              <div style={{ marginTop: 8 }}>
                <KeywordsManager ownerType={OwnerType.PROJECT} ownerId={data.id} />
              </div>
            </div>
          </Card>

          {data.children && data.children.length > 0 && (
            <Card title="Sub-proyectos" style={{ marginTop: 16 }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {data.children.map((c) => (
                  <li key={c.id} style={{ padding: '4px 0' }}>
                    <Link to={`/proyectos/${c.id}`}>{c.name}</Link>
                    {c.status && (
                      <Tag style={{ marginLeft: 8 }}>{c.status}</Tag>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Ventas" style={{ marginBottom: 16 }}>
            {salesQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {salesQuery.data.data.map((s) => (
                  <li key={s.id} style={{ padding: '4px 0' }}>
                    <Link to={`/ventas/${s.id}`}>
                      {s.saleNumber ? `${s.saleNumber} · ` : ''}
                      {s.name}
                    </Link>
                    {s.amount && (
                      <Tag style={{ marginLeft: 8 }} color="blue">
                        {parseFloat(s.amount).toLocaleString('es-MX')}{' '}
                        {s.currency?.code ?? ''}
                      </Tag>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin ventas asociadas</div>
            )}
          </Card>

          <Card title="Actividades recientes">
            {activitiesQuery.data?.data.length ? (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {activitiesQuery.data.data.map((a) => (
                  <li key={a.id} style={{ padding: '4px 0' }}>
                    <Link to={`/actividades/${a.id}`}>
                      {a.type ?? 'Actividad'} —{' '}
                      {a.notes?.slice(0, 60)}
                      {a.notes && a.notes.length > 60 ? '…' : ''}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ color: '#888' }}>Sin actividades</div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
